import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { Loader2, ShieldOff, Truck, Plus, Trash2, X, Save, GripVertical, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const inputCls = "bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

const UNITS = [
  { value: 'g', label: 'grams (g)' },
  { value: 'kg', label: 'kg' },
  { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' },
];

const toGrams = (v, unit) => {
  const n = Number(v);
  if (!n) return 0;
  return n * ({ g: 1, kg: 1000, lb: 453.592, oz: 28.3495 }[unit] || 1);
};

export default function AdminShipping() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null); // rate being created/edited
  const [deleteTarget, setDeleteTarget] = useState(null);
  const qc = useQueryClient();

  const { data: rates = [], isLoading } = useQuery({
    queryKey: ['shippingRates'],
    queryFn: async () => {
      const list = await base44.entities.ShippingRate.list('sort_order', 100);
      return list || [];
    },
    enabled: !!currentUser?.role && currentUser.role === 'admin',
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      if (id) return base44.entities.ShippingRate.update(id, data);
      return base44.entities.ShippingRate.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shippingRates'] });
      setEditing(null);
    },
    onError: (e) => toast({ title: 'Could not save rate', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.ShippingRate.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shippingRates'] }),
    onError: (e) => toast({ title: 'Could not delete rate', description: e.message, variant: 'destructive' }),
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto flex items-end justify-between flex-wrap gap-4">
          <div>
            <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
            </Link>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">SYSTEM &amp; INTEGRATIONS</p>
            <h1 className="text-white font-black text-3xl md:text-4xl uppercase">POSTAL SETTINGS</h1>
            <p className="text-white/30 text-xs mt-2">
              Define shipping bands by weight. At checkout the cart's total product weight picks the matching band.
              Leave "Max Weight" blank for an "and above" band.
            </p>
          </div>
          <button
            onClick={() => setEditing({ name: '', zone: 'UK', min_weight_g: 0, max_weight_g: '', rate: '', active: true, sort_order: rates.length })}
            className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> ADD RATE
          </button>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : rates.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Truck className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/40 text-sm mb-2">No shipping rates configured yet.</p>
            <p className="text-white/20 text-xs">Add a rate to start charging shipping at checkout.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rates.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 sm:gap-4 bg-[#111115] border border-white/5 p-4">
                <GripVertical className="w-4 h-4 text-white/10 shrink-0" />
                <div className="flex-1 min-w-0 w-full sm:w-auto">
                  <p className="text-white font-bold text-sm">{r.name}</p>
                  <p className="text-white/30 text-xs mt-0.5">
                    {r.zone} · {r.min_weight_g || 0}g
                    {r.max_weight_g == null || r.max_weight_g === '' ? '+' : `– ${r.max_weight_g}g`}
                  </p>
                </div>
                <p className="text-primary font-black text-lg shrink-0">£{Number(r.rate).toFixed(2)}</p>
                <span className={`text-[10px] font-bold tracking-widest px-2 py-1 shrink-0 ${r.active !== false ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                  {r.active !== false ? 'ACTIVE' : 'DISABLED'}
                </span>
                <button onClick={() => setEditing(r)} className="text-white/40 hover:text-white text-xs font-bold tracking-widest transition-colors shrink-0">EDIT</button>
                <button onClick={() => setDeleteTarget(r)} className="text-white/20 hover:text-red-400 transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 bg-white/[0.02] border border-white/5 p-5">
          <p className="text-primary text-[10px] font-bold tracking-widest mb-2">HOW IT WORKS</p>
          <ul className="text-white/40 text-xs space-y-1.5 leading-relaxed list-disc pl-4">
            <li>Product weights are set per product in the Products admin (Weight + Weight Unit fields).</li>
            <li>At checkout, all product weights are summed (converted to grams) and matched against these bands.</li>
            <li>The first active band whose range contains the total weight wins. If none match exactly, the highest band is used.</li>
            <li>Orders containing only bookings or digital gift cards are never charged shipping.</li>
          </ul>
        </div>
      </section>

      {editing && (
        <RateModal
          rate={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => saveMutation.mutate({ id: editing.id, data: payload })}
          saving={saveMutation.isPending}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete shipping rate"
        description={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteMutation.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function RateModal({ rate, onClose, onSave, saving }) {
  const isEdit = !!rate.id;
  const [form, setForm] = useState({
    name: rate.name || '',
    zone: rate.zone || 'UK',
    min_weight_g: rate.min_weight_g ?? 0,
    max_weight_g: rate.max_weight_g ?? '',
    rate: rate.rate ?? '',
    active: rate.active !== false,
    sort_order: rate.sort_order ?? 0,
  });
  const [unit, setUnit] = useState('g');
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) { setError('Enter a name for this rate.'); return; }
    if (form.rate === '' || Number(form.rate) < 0) { setError('Enter a valid rate.'); return; }
    const payload = {
      name: form.name.trim(),
      zone: form.zone.trim() || 'UK',
      min_weight_g: Number(form.min_weight_g) || 0,
      max_weight_g: form.max_weight_g === '' || form.max_weight_g === null ? null : Number(form.max_weight_g),
      rate: Number(form.rate),
      active: form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    if (payload.max_weight_g != null && payload.max_weight_g < payload.min_weight_g) {
      setError('Max weight must be greater than or equal to min weight.'); return;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
          <p className="text-primary text-[10px] font-bold tracking-widest">{isEdit ? 'EDIT RATE' : 'NEW RATE'}</p>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-5 space-y-4">
          <div>
            <label className={labelCls}>NAME *</label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} placeholder="e.g. UK Mainland 0–2kg" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>ZONE</label>
              <Input value={form.zone} onChange={e => set('zone', e.target.value)} className={inputCls} placeholder="UK Mainland" />
            </div>
            <div>
              <label className={labelCls}>SORT ORDER</label>
              <Input type="number" min="0" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>MIN WEIGHT (g) *</label>
              <Input type="number" min="0" value={form.min_weight_g} onChange={e => set('min_weight_g', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>MAX WEIGHT (g)</label>
              <Input type="number" min="0" value={form.max_weight_g} onChange={e => set('max_weight_g', e.target.value)} className={inputCls} placeholder="Blank = and above" />
            </div>
            <div>
              <label className={labelCls}>RATE (GBP) *</label>
              <Input type="number" min="0" step="0.01" value={form.rate} onChange={e => set('rate', e.target.value)} className={inputCls} placeholder="0.00" />
            </div>
          </div>
          <p className="text-white/20 text-[10px]">Weights are stored in grams. 1kg = 1000g, 1lb ≈ 453.6g, 1oz ≈ 28.3g.</p>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 accent-primary" />
            <span className="text-white/60 text-xs">Active (available at checkout)</span>
          </label>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <div className="flex gap-3 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-all">CANCEL</button>
            <button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEdit ? 'SAVE' : 'CREATE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}